import { audioCtx } from '../audio/context';

/**
 * Synthesised drum breaks.
 *
 * These exist so Happy Chopper has something to chop the moment you open it, without
 * shipping sample data or asking anyone to hunt down clearable audio. They are
 * deliberately dry and transient-heavy: that makes the chop grid obvious, gives
 * the tempo detector real accent structure to work with, and leaves room for
 * the track filters and sends to do something audible.
 *
 * Patterns are written as one string per drum voice, `.` for a rest and `1`-`9`
 * for velocity, at sixteenth-note resolution. A lane shorter than the loop
 * simply cycles, so a steady hat lane is sixteen characters and a two-bar kick
 * lane is thirty-two.
 */

const STEPS_PER_BAR = 16;

type VoiceName = 'kick' | 'snare' | 'ghost' | 'hat' | 'open' | 'rim' | 'clap' | 'tom' | 'ride';

interface VoiceSpec {
  /** How long the hit rings for, seconds. */
  seconds: number;
  pan: number;
  /** Fills `out` with one hit at velocity `v` (0..1). */
  render(out: Float32Array, sr: number, v: number): void;
}

/**
 * Noise differencing (`n - previous`) is a one-pole high pass — a +6 dB/octave
 * tilt. Applied once it thins a snare, twice it turns white noise into
 * something that reads as a cymbal.
 */
function brightNoise(stages: number): () => number {
  const last = new Float32Array(stages);
  return () => {
    let x = Math.random() * 2 - 1;
    for (let s = 0; s < stages; s++) {
      const y = x - last[s];
      last[s] = x;
      x = y;
    }
    return x;
  };
}

const VOICES: Record<VoiceName, VoiceSpec> = {
  kick: {
    seconds: 0.4,
    pan: 0,
    render(out, sr, v) {
      let phase = 0;
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        // Pitch sweep from the beater click down to the fundamental.
        const f = 48 + 115 * Math.exp(-t * 34);
        phase += (2 * Math.PI * f) / sr;
        const body = Math.sin(phase) * Math.exp(-t * 11);
        const click = Math.exp(-t * 420) * 0.3;
        out[i] = (body + click) * v * 0.95;
      }
    },
  },
  snare: {
    seconds: 0.3,
    pan: 0,
    render(out, sr, v) {
      const noise = brightNoise(1);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        const rattle = noise() * Math.exp(-t * 21);
        const shell =
          (Math.sin(2 * Math.PI * 185 * t) * 0.6 + Math.sin(2 * Math.PI * 331 * t) * 0.4) *
          Math.exp(-t * 27);
        out[i] = (rattle * 0.6 + shell * 0.4) * v * 0.8;
      }
    },
  },
  ghost: {
    seconds: 0.09,
    pan: -0.1,
    render(out, sr, v) {
      const noise = brightNoise(1);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        const rattle = noise() * Math.exp(-t * 60);
        const shell = Math.sin(2 * Math.PI * 195 * t) * Math.exp(-t * 70);
        out[i] = (rattle * 0.7 + shell * 0.3) * v * 0.45;
      }
    },
  },
  hat: {
    seconds: 0.07,
    pan: -0.22,
    render(out, sr, v) {
      const noise = brightNoise(2);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        out[i] = noise() * Math.exp(-t * 95) * v * 0.3;
      }
    },
  },
  open: {
    seconds: 0.34,
    pan: 0.26,
    render(out, sr, v) {
      const noise = brightNoise(2);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        out[i] = noise() * Math.exp(-t * 9) * v * 0.26;
      }
    },
  },
  rim: {
    seconds: 0.06,
    pan: 0.3,
    render(out, sr, v) {
      const noise = brightNoise(2);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        const tick = Math.sin(2 * Math.PI * 1720 * t) * Math.exp(-t * 240);
        out[i] = (tick * 0.7 + noise() * Math.exp(-t * 300) * 0.3) * v * 0.55;
      }
    },
  },
  clap: {
    seconds: 0.3,
    pan: 0.12,
    render(out, sr, v) {
      const noise = brightNoise(1);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        // Three staggered bursts, then a short room tail — the thing that makes
        // a clap read as hands rather than a snare.
        let env = Math.exp(-t * 15) * 0.45;
        for (const off of [0, 0.009, 0.018]) {
          if (t >= off) env = Math.max(env, Math.exp(-(t - off) * 190));
        }
        out[i] = noise() * env * v * 0.5;
      }
    },
  },
  tom: {
    seconds: 0.35,
    pan: -0.3,
    render(out, sr, v) {
      let phase = 0;
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        const f = 112 + 95 * Math.exp(-t * 16);
        phase += (2 * Math.PI * f) / sr;
        out[i] = Math.sin(phase) * Math.exp(-t * 9) * v * 0.75;
      }
    },
  },
  ride: {
    seconds: 0.55,
    pan: 0.34,
    render(out, sr, v) {
      const noise = brightNoise(2);
      for (let i = 0; i < out.length; i++) {
        const t = i / sr;
        const wash = noise() * Math.exp(-t * 4.5) * 0.5;
        const ping =
          (Math.sin(2 * Math.PI * 3100 * t) * 0.5 + Math.sin(2 * Math.PI * 4630 * t) * 0.5) *
          Math.exp(-t * 26);
        out[i] = (wash + ping * 0.4) * v * 0.22;
      }
    },
  },
};

export interface DemoKit {
  id: string;
  name: string;
  description: string;
  bpm: number;
  bars: number;
  lanes: Partial<Record<VoiceName, string>>;
  /**
   * Room ambience, 0..1. The classic funk breaks were cut live in a room with
   * a couple of overheads, and that space is a big part of why they chop well
   * — the tail of each hit bleeds into the next slice.
   */
  room?: number;
}

export const DEMO_KITS: DemoKit[] = [
  {
    id: 'amen',
    name: 'Amen-style Break',
    description: '4 bars, 136 BPM — the classic funk-break structure, synthesised',
    bpm: 136,
    bars: 4,
    room: 0.55,
    lanes: {
      // The rhythm that made the original endlessly chop-able: two identical
      // bars, then bar 3 pushes its second snare a sixteenth late, then bar 4
      // drops the downbeat kick entirely. That asymmetry is what gives you
      // four bars of genuinely different material to rearrange.
      kick:
        '9.........8.....' +
        '9.........8.....' +
        '9.........8.....' +
        '..........8.....',
      snare:
        '....9.......9...' +
        '....9.......9...' +
        '....9.........9.' +
        '....9.........9.',
      ghost:
        '..2....3..2....2' +
        '..2....3..2....3' +
        '..2....3..2..2..' +
        '..3....2..2..3..',
      // Ride through the whole thing rather than hats — that's the sound.
      ride: '6.4.5.4.6.4.5.4.',
    },
  },
  {
    id: 'boombap',
    name: 'Boom Bap',
    description: 'Dusty 90s swing, heavy backbeat',
    bpm: 90,
    bars: 2,
    lanes: {
      kick: '9.....7...8.....' + '9..5..7....8....',
      snare: '....9.......9...',
      ghost: '.......3......2.' + '..2....3......3.',
      hat: '7.5.7.5.7.5.7.5.',
      open: '................' + '..............6.',
    },
  },
  {
    id: 'jungle',
    name: 'Jungle Chop',
    description: 'Chopped break, ghost notes everywhere',
    bpm: 172,
    bars: 2,
    lanes: {
      kick: '9.........8.....' + '9...9.....8.....',
      snare: '....9......8....' + '....9.......9.6.',
      ghost: '..2...3..2...3.2' + '..2...3.....2..3',
      hat: '6.4.6.4.6.4.6.4.',
      open: '................' + '..............7.',
    },
  },
  {
    id: 'fourfloor',
    name: 'Four On The Floor',
    description: 'Straight house kick, clap on the twos',
    bpm: 126,
    bars: 2,
    lanes: {
      kick: '9...9...9...9...',
      clap: '....8.......8...',
      hat: '..5...5...5...5.' + '3.5.3.5.3.5.3.5.',
      open: '................' + '..............7.',
      rim: '................' + '.......4.......4',
    },
  },
  {
    id: 'trap',
    name: 'Trap Rollers',
    description: 'Half-time, rolling hats, sub kick',
    bpm: 140,
    bars: 2,
    lanes: {
      kick: '9......7..8.....' + '9....9....8.....',
      snare: '........9.......',
      hat: '7.5.7.5.7.5.7555' + '7.5.7555.7.5.7.5',
      open: '................' + '............7...',
    },
  },
  {
    id: 'funk',
    name: 'Funk Break',
    description: 'Syncopated kick, ghosted snare, tom fill',
    bpm: 104,
    bars: 2,
    lanes: {
      kick: '9.....7...9.....' + '9..7..9...9.....',
      snare: '....9.......9...' + '....9......9.9..',
      ghost: '..2..3..2..3..2.' + '..2..3.....2...3',
      hat: '7.5.7.5.7.5.7.5.',
      tom: '................' + '..............65',
    },
  },
  {
    id: 'halftime',
    name: 'Half-Time Stomp',
    description: 'Slow and heavy, wide open snare',
    bpm: 82,
    bars: 2,
    lanes: {
      kick: '9.........9.....' + '9....9....9.....',
      snare: '........9.......',
      hat: '6...5...6...5...',
      open: '................' + '..............7.',
    },
  },
  {
    id: 'breakbeat',
    name: 'Breakbeat',
    description: 'Big beat bounce, double kick turnaround',
    bpm: 136,
    bars: 2,
    lanes: {
      kick: '9...9.....9.....' + '9.........9..9..',
      snare: '....9.......9...' + '....9.......9.7.',
      ghost: '..2....3..2....3',
      hat: '6.5.6.5.6.5.6.5.',
      open: '................' + '..............6.',
    },
  },
  {
    id: 'disco',
    name: 'Disco Shuffle',
    description: 'Open hats on the ands, ride wash',
    bpm: 118,
    bars: 2,
    lanes: {
      kick: '9...9...9...9...',
      snare: '....9.......9...',
      open: '..6...6...6...6.',
      ride: '5.4.5.4.5.4.5.4.',
      tom: '................' + '..............76',
    },
  },
];

/** Renders a kit to an AudioBuffer. `bars` repeats or truncates the lanes. */
export function renderKit(kit: DemoKit, overrides: { bpm?: number; bars?: number } = {}): AudioBuffer {
  const ctx = audioCtx();
  const sr = ctx.sampleRate;
  const bpm = overrides.bpm ?? kit.bpm;
  const bars = overrides.bars ?? kit.bars;
  const stepSec = 60 / bpm / 4;
  const steps = Math.max(1, Math.round(bars * STEPS_PER_BAR));
  const length = Math.ceil(steps * stepSec * sr);

  const buf = ctx.createBuffer(2, length, sr);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);
  const scratch = new Float32Array(Math.ceil(0.6 * sr));

  for (const [name, lane] of Object.entries(kit.lanes) as [VoiceName, string][]) {
    const spec = VOICES[name];
    if (!spec || !lane.length) continue;
    const hitLen = Math.min(scratch.length, Math.ceil(spec.seconds * sr));
    const lGain = 1 - Math.max(0, spec.pan);
    const rGain = 1 + Math.min(0, spec.pan);

    for (let s = 0; s < steps; s++) {
      const c = lane[s % lane.length];
      if (c === '.' || c === undefined) continue;
      const velocity = (c.charCodeAt(0) - 48) / 9;
      if (!(velocity > 0)) continue;

      const hit = scratch.subarray(0, hitLen);
      hit.fill(0);
      spec.render(hit, sr, velocity);

      const start = Math.floor(s * stepSec * sr);
      const n = Math.min(hitLen, length - start);
      for (let i = 0; i < n; i++) {
        L[start + i] += hit[i] * lGain;
        R[start + i] += hit[i] * rGain;
      }
    }
  }

  if (kit.room) applyRoom(L, R, sr, kit.room);

  // Summing lanes can push past unity; normalise rather than clip, so the
  // detector and the chop grid see clean transients.
  let peak = 0;
  for (let i = 0; i < length; i++) {
    peak = Math.max(peak, Math.abs(L[i]), Math.abs(R[i]));
  }
  if (peak > 0.92) {
    const g = 0.92 / peak;
    for (let i = 0; i < length; i++) {
      L[i] *= g;
      R[i] *= g;
    }
  }
  return buf;
}

/**
 * Cheap room ambience: a handful of panned early reflections plus a short
 * decaying tail, both rolled off at the top.
 *
 * Not trying to be a reverb — the point is that a break recorded in a room has
 * each hit bleeding into the next, which is exactly what makes the tail of one
 * slice sit against the head of another when you rearrange them.
 */
function applyRoom(L: Float32Array, R: Float32Array, sr: number, amount: number): void {
  const taps: [number, number, number][] = [
    // delay seconds, gain, pan
    [0.0113, 0.5, -0.35],
    [0.0171, 0.44, 0.4],
    [0.0237, 0.36, -0.22],
    [0.0313, 0.29, 0.28],
    [0.0431, 0.22, -0.16],
    [0.0587, 0.16, 0.18],
    [0.0713, 0.12, -0.1],
    [0.0917, 0.08, 0.12],
  ];
  const n = L.length;
  const wetL = new Float32Array(n);
  const wetR = new Float32Array(n);

  for (const [delay, gain, pan] of taps) {
    const d = Math.floor(delay * sr);
    if (d >= n) continue;
    const gl = gain * (1 - Math.max(0, pan));
    const gr = gain * (1 + Math.min(0, pan));
    for (let i = d; i < n; i++) {
      const dry = (L[i - d] + R[i - d]) * 0.5;
      wetL[i] += dry * gl;
      wetR[i] += dry * gr;
    }
  }

  // One-pole low pass — rooms are darker than the source.
  let a = 0;
  let b = 0;
  const k = 0.42;
  for (let i = 0; i < n; i++) {
    a += (wetL[i] - a) * k;
    b += (wetR[i] - b) * k;
    L[i] += a * amount;
    R[i] += b * amount;
  }
}

/** Convenience wrapper used by the dev console harness. */
export function makeDemoLoop(bpm = 120, bars = 2, _beatsPerBar = 4): AudioBuffer {
  return renderKit(DEMO_KITS[0], { bpm, bars });
}
